'use client';

import {useEffect, useRef, useState} from 'react';
import VimeoPlayer from '@vimeo/player';
import clsx from 'clsx';

import {usePrefersReducedMotion} from '../../hooks/usePrefersReducedMotion';

type VideoType = {
	title: string;
	id: string;
};

type Props = {
	video?: VideoType;
	autoplay?: boolean;
	cover?: boolean;
	paused?: boolean;
	darken?: boolean;
};

export const VideoPlayer = ({
	video,
	autoplay = false,
	cover,
	paused,
	darken,
}: Props) => {
	const hasReducedMotionPreference = usePrefersReducedMotion();

	const [hasFetchedDimensions, setHasFetchedDimensions] = useState(false);

	const [{height, width}, setDimensions] = useState({
		height: 0,
		width: 0,
	});

	const iframeRef = useRef<HTMLIFrameElement>(null);
	const videoContainerRef = useRef<HTMLDivElement>(null);
	const playerRef = useRef<VimeoPlayer | null>(null);
	const isInitialMount = useRef(true);

	const [parentWidth, setParentWidth] = useState(0);
	const [parentHeight, setParentHeight] = useState(0);

	useEffect(() => {
		if (!videoContainerRef.current) {
			return;
		}

		const onResize = () => {
			setParentWidth(
				videoContainerRef.current?.parentElement?.scrollWidth ?? 0,
			);
			setParentHeight(
				videoContainerRef.current?.parentElement?.scrollHeight ?? 0,
			);
		};

		onResize();
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [videoContainerRef, height, width, iframeRef]);

	// Initialize Vimeo player ref
	useEffect(() => {
		if (!iframeRef.current) {
			return;
		}
		playerRef.current = new VimeoPlayer(iframeRef.current);
	}, [iframeRef]);

	// Control play/pause via paused prop
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		if (paused) {
			playerRef.current?.pause();
		} else {
			playerRef.current?.play();
		}
	}, [paused]);

	const videoParams = new URLSearchParams({
		autoplay: autoplay && !hasReducedMotionPreference ? '1' : '0',
		loop: autoplay && !hasReducedMotionPreference ? '1' : '0',
		autopause: autoplay && !hasReducedMotionPreference ? '0' : '1',
		title: '0',
		byline: '0',
		portrait: '0',
		controls: cover ? '0' : '1',
		muted: autoplay || cover ? '1' : '0',
		fullscreen: '1',
		background: '0',
	});

	// Fetch video dimensions from Vimeo API
	useEffect(() => {
		if (!video?.id) {
			return;
		}

		const getVideoDimensions = async () => {
			try {
				const response = await fetch(
					`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${video.id}`,
				);
				if (!response.ok) {
					throw new Error('Could not fetch video dimensions');
				}

				const dimensions = await response.json();
				setDimensions(dimensions);
				setHasFetchedDimensions(true);
			} catch (error) {
				console.error(error);
				setDimensions({height: 0, width: 0});
				setHasFetchedDimensions(false);
			}
		};

		getVideoDimensions();
	}, [video?.id]);

	const aspectRatio = cover
		? `${parentWidth}/${parentHeight}`
		: `${width}/${height}`;

	const style = {
		height: '100%',
		width: '100%',
	};
	const parentAspectRatio = parentWidth / parentHeight;
	const videoAspectRatio = width / height;
	if (videoAspectRatio > parentAspectRatio) {
		const widthPercentRelativeToHeight = (100 * width) / height;
		const widthPercent =
			(widthPercentRelativeToHeight * parentHeight) / parentWidth;
		style.width = `${Math.ceil(widthPercent)}%`;
	} else if (videoAspectRatio < parentAspectRatio) {
		const heightPercentRelativeToWidth = (100 * height) / width;
		const heightPercent =
			(heightPercentRelativeToWidth * parentWidth) / parentHeight;
		style.height = `${Math.ceil(heightPercent)}%`;
	}

	return (
		<div
			ref={videoContainerRef}
			className={clsx(
				'relative',
				'flex',
				'justify-center',
				'items-center',
				'overflow-hidden',
				'max-w-full',
				'max-h-full',
				darken && [
					'after:absolute',
					'after:w-full',
					'after:h-full',
					'after:bg-black/20',
					'after:top-0',
					'after:z-50',
					'isolate',
				],
			)}
			style={{aspectRatio: hasFetchedDimensions ? aspectRatio : '16/9'}}
		>
			{video?.id && (
				<iframe
					name="vimeo video avspiller"
					title={video.title}
					src={`https://player.vimeo.com/video/${video.id}?${videoParams.toString()}`}
					allow="autoplay; muted; fullscreen;"
					style={style}
					className={clsx('absolute', cover && 'pointer-events-none')}
					ref={iframeRef}
					{...(cover && {tabIndex: -1})}
				></iframe>
			)}
		</div>
	);
};
